// FE-SCRM-055 — Settings → Sales CRM preferences (per-user).
//
// Three knobs scoped to the signed-in user (not the brokerage):
// default Renewal Board geometry, default landing tab on the Sales
// CRM, default Activities sort. v1 stores these in localStorage —
// see ``userPrefs.ts`` for the storage adapter.

import { useSalesCrmUserPrefs } from "./userPrefs";
import type { SalesCrmUserPreferences } from "./types";


type GeometryOption = SalesCrmUserPreferences["renewal_board_geometry"];
type TabOption = SalesCrmUserPreferences["default_landing_tab"];
type SortOption = SalesCrmUserPreferences["default_activities_sort"];


const TAB_OPTIONS: { value: TabOption; label: string }[] = [
  { value: "pipeline", label: "Pipeline" },
  { value: "prospects", label: "Prospects" },
  { value: "activities", label: "Activities" },
  { value: "reports", label: "Reports" },
  { value: "settings", label: "Settings" },
];


const GEOMETRY_OPTIONS: { value: GeometryOption; label: string }[] = [
  { value: "grid", label: "Grid" },
  { value: "timeline", label: "Timeline" },
];


const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "due_at_asc", label: "Due date (earliest first)" },
  { value: "due_at_desc", label: "Due date (latest first)" },
  { value: "created_desc", label: "Recently added" },
];


export function UserPreferencesSection(): JSX.Element {
  const [prefs, updatePrefs] = useSalesCrmUserPrefs();

  return (
    <section
      data-testid="settings-user-preferences"
      className="flex flex-col gap-3"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-text-primary">
          Sales CRM preferences
        </h2>
        <p className="text-xs text-text-secondary">
          These preferences only affect your account.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <PrefRow
          label="Default landing tab"
          value={prefs.default_landing_tab}
          options={TAB_OPTIONS}
          onChange={(v) => updatePrefs({ default_landing_tab: v as TabOption })}
          testId="pref-default-landing-tab"
        />
        <PrefRow
          label="Default Renewal Board view"
          value={prefs.renewal_board_geometry}
          options={GEOMETRY_OPTIONS}
          onChange={(v) =>
            updatePrefs({ renewal_board_geometry: v as GeometryOption })}
          testId="pref-renewal-board-geometry"
        />
        <PrefRow
          label="Default Activities sort"
          value={prefs.default_activities_sort}
          options={SORT_OPTIONS}
          onChange={(v) =>
            updatePrefs({ default_activities_sort: v as SortOption })}
          testId="pref-default-activities-sort"
        />
      </div>
    </section>
  );
}


function PrefRow<T extends string>({
  label, value, options, onChange, testId,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
  testId: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-text-secondary">
      {label}
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="rounded border border-border-subtle bg-raised px-2 py-1.5 text-sm text-text-primary"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
